import React, { useRef, useEffect } from 'react';
import { Eye } from 'lucide-react';

/**
 * Компонент для визуализации помещения
 */
const RoomVisualization = ({ results, formData }) => {
  const canvasRef = useRef(null);
  const { roomShape, measurementUnit } = formData;
  
  // Конвертация из метров в пиксели для масштабирования
  const scale = 40; // 1 метр = 40 пикселей
  
  useEffect(() => {
    if (!canvasRef.current || !results) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Очищаем канвас
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Устанавливаем стили по умолчанию
    ctx.strokeStyle = '#a21caf'; // fuchsia-600
    ctx.lineWidth = 4;
    ctx.lineJoin = 'round';
    
    // Функция для конвертации значений из выбранных единиц измерения в метры
    const toMeters = (value) => {
      switch (measurementUnit) {
        case 'centimeters': return value / 100;
        case 'millimeters': return value / 1000;
        default: return value;
      }
    };
    
    // Получаем размеры комнаты в метрах
    let width = 0;
    let length = 0;
    let maxDimension = 0; // Максимальный размер для масштабирования
    
    switch (roomShape) {
      case 'rectangle':
        length = toMeters(parseFloat(formData.length) || 0);
        width = toMeters(parseFloat(formData.width) || 0);
        maxDimension = Math.max(length, width);
        break;
      case 'l-shaped':
        length = toMeters(parseFloat(formData.mainLength) || 0);
        width = toMeters(parseFloat(formData.mainWidth) || 0);
        const extensionLength = toMeters(parseFloat(formData.extensionLength) || 0);
        const extensionWidth = toMeters(parseFloat(formData.extensionWidth) || 0);
        maxDimension = Math.max(length, width, extensionLength, extensionWidth);
        break;
      case 'angled':
        length = toMeters(parseFloat(formData.baseLength) || 0);
        width = toMeters(parseFloat(formData.baseWidth) || 0);
        maxDimension = Math.max(length, width);
        break;
      case 'bay-window':
        length = toMeters(parseFloat(formData.roomLength) || 0);
        width = toMeters(parseFloat(formData.roomWidth) || 0);
        maxDimension = Math.max(length, width);
        break;
      case 'niche':
        length = toMeters(parseFloat(formData.roomLengthWithNiche) || 0);
        width = toMeters(parseFloat(formData.roomWidthWithNiche) || 0);
        maxDimension = Math.max(length, width);
        break;
      case 'column':
        length = toMeters(parseFloat(formData.roomLengthWithColumn) || 0);
        width = toMeters(parseFloat(formData.roomWidthWithColumn) || 0);
        maxDimension = Math.max(length, width);
        break;
      case 'circle':
        const diameter = toMeters(parseFloat(formData.diameter) || 0);
        length = diameter;
        width = diameter;
        maxDimension = diameter;
        break;
      default:
        length = width = 5; // Значения по умолчанию
        maxDimension = 5;
    }
    
    // Вычисляем масштаб чтобы комната занимала примерно 80% канваса
    const dynamicScale = Math.min(
      (canvas.width * 0.8) / length,
      (canvas.height * 0.8) / width
    );
    
    // Центрируем рисунок на канвасе
    const offsetX = (canvas.width - length * dynamicScale) / 2;
    const offsetY = (canvas.height - width * dynamicScale) / 2;
    
    // Рисуем комнату в зависимости от формы
    switch (roomShape) {
      case 'rectangle':
        // Рисуем прямоугольник
        ctx.beginPath();
        ctx.rect(offsetX, offsetY, length * dynamicScale, width * dynamicScale);
        ctx.stroke();
        
        // Добавляем размеры
        addDimensions(ctx, length, width, offsetX, offsetY, dynamicScale);
        
        // Рисуем элементы комнаты (окна, двери), если они включены
        if (formData.includeWindows) {
          drawWindows(ctx, formData, offsetX, offsetY, length, width, dynamicScale, toMeters);
        }
        
        if (formData.includeDoors) {
          drawDoors(ctx, formData, offsetX, offsetY, length, width, dynamicScale, toMeters);
        }
        break;
        
      case 'l-shaped':
        // Получаем размеры Г-образной комнаты
        const mainLength = toMeters(parseFloat(formData.mainLength) || 0);
        const mainWidth = toMeters(parseFloat(formData.mainWidth) || 0);
        const extensionLength = toMeters(parseFloat(formData.extensionLength) || 0);
        const extensionWidth = toMeters(parseFloat(formData.extensionWidth) || 0);
        
        // Рисуем Г-образную комнату
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY);
        ctx.lineTo(offsetX + mainLength * dynamicScale, offsetY);
        ctx.lineTo(offsetX + mainLength * dynamicScale, offsetY + extensionWidth * dynamicScale);
        ctx.lineTo(offsetX + (mainLength - extensionLength) * dynamicScale, offsetY + extensionWidth * dynamicScale);
        ctx.lineTo(offsetX + (mainLength - extensionLength) * dynamicScale, offsetY + mainWidth * dynamicScale);
        ctx.lineTo(offsetX, offsetY + mainWidth * dynamicScale);
        ctx.closePath();
        ctx.stroke();
        
        // Добавляем размеры
        ctx.font = '12px Arial';
        ctx.fillStyle = '#6b7280'; // gray-500
        ctx.fillText(`${mainLength.toFixed(1)} м`, offsetX + mainLength * dynamicScale / 2 - 15, offsetY - 10);
        ctx.fillText(`${mainWidth.toFixed(1)} м`, offsetX - 30, offsetY + mainWidth * dynamicScale / 2 + 5);
        ctx.fillText(`${extensionLength.toFixed(1)} м`, offsetX + (mainLength - extensionLength / 2) * dynamicScale - 15, offsetY + extensionWidth * dynamicScale + 20);
        ctx.fillText(`${extensionWidth.toFixed(1)} м`, offsetX + mainLength * dynamicScale + 10, offsetY + extensionWidth * dynamicScale / 2);
        break;
        
      case 'circle':
        // Получаем диаметр
        const diameter = toMeters(parseFloat(formData.diameter) || 0);
        const radius = diameter / 2;
        
        // Рисуем круг
        ctx.beginPath();
        ctx.arc(
          canvas.width / 2, 
          canvas.height / 2, 
          radius * dynamicScale, 
          0, 
          Math.PI * 2
        );
        ctx.stroke();
        
        // Добавляем размеры
        ctx.font = '12px Arial';
        ctx.fillStyle = '#6b7280'; // gray-500
        ctx.fillText(`Ø ${diameter.toFixed(1)} м`, canvas.width / 2 - 20, canvas.height / 2 - radius * dynamicScale - 10);
        break;
        
      case 'angled':
        // Получаем размеры комнаты со скошенным углом
        const baseLength = toMeters(parseFloat(formData.baseLength) || 0);
        const baseWidth = toMeters(parseFloat(formData.baseWidth) || 0);
        const cutoutLength = toMeters(parseFloat(formData.cutoutLength) || 0);
        const cutoutWidth = toMeters(parseFloat(formData.cutoutWidth) || 0);
        
        // Рисуем комнату со скошенным углом
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY);
        ctx.lineTo(offsetX + baseLength * dynamicScale, offsetY);
        ctx.lineTo(offsetX + baseLength * dynamicScale, offsetY + baseWidth * dynamicScale);
        ctx.lineTo(offsetX + cutoutLength * dynamicScale, offsetY + baseWidth * dynamicScale);
        ctx.lineTo(offsetX, offsetY + (baseWidth - cutoutWidth) * dynamicScale);
        ctx.closePath();
        ctx.stroke();
        
        // Добавляем размеры
        ctx.font = '12px Arial';
        ctx.fillStyle = '#6b7280'; // gray-500
        ctx.fillText(`${baseLength.toFixed(1)} м`, offsetX + baseLength * dynamicScale / 2 - 15, offsetY - 10);
        break;
        
      case 'bay-window':
        // Получаем размеры комнаты с эркером
        const roomLength = toMeters(parseFloat(formData.roomLength) || 0);
        const roomWidth = toMeters(parseFloat(formData.roomWidth) || 0);
        const bayWindowDepth = toMeters(parseFloat(formData.bayWindowDepth) || 0);
        const bayWindowWidth = toMeters(parseFloat(formData.bayWindowWidth) || 0);
        
        // Рисуем основную комнату
        ctx.beginPath();
        ctx.rect(offsetX, offsetY, roomLength * dynamicScale, roomWidth * dynamicScale);
        ctx.stroke();
        
        // Рисуем эркер
        const bayX = offsetX + (roomLength - bayWindowWidth) / 2 * dynamicScale;
        const bayY = offsetY + roomWidth * dynamicScale;
        
        ctx.beginPath();
        ctx.rect(
          bayX, 
          bayY, 
          bayWindowWidth * dynamicScale, 
          bayWindowDepth * dynamicScale
        );
        ctx.stroke();
        break;
      
      default:
        // Просто прямоугольник для других форм
        ctx.beginPath();
        ctx.rect(offsetX, offsetY, length * dynamicScale, width * dynamicScale);
        ctx.stroke();
    }
    
    // Добавляем подпись с площадью
    ctx.font = 'bold 14px Arial';
    ctx.fillStyle = '#a21caf'; // fuchsia-600
    ctx.fillText(
      `Площадь: ${results.floorArea.toFixed(2)} м²`, 
      canvas.width / 2 - 60, 
      canvas.height - 20
    );
    
  }, [formData, results]);
  
  // Функция добавления размеров на схему
  const addDimensions = (ctx, length, width, offsetX, offsetY, scale) => {
    ctx.font = '12px Arial';
    ctx.fillStyle = '#6b7280'; // gray-500
    
    // Добавляем длину (верхний край)
    ctx.fillText(
      `${length.toFixed(1)} м`, 
      offsetX + length * scale / 2 - 15, 
      offsetY - 10
    );
    
    // Добавляем ширину (левый край)
    ctx.fillText(
      `${width.toFixed(1)} м`, 
      offsetX - 30, 
      offsetY + width * scale / 2 + 5
    );
  };
  
  // Функция рисования окон
  const drawWindows = (ctx, formData, offsetX, offsetY, roomLength, roomWidth, scale, toMeters) => {
    if (!formData.includeWindows) return;
    
    const count = parseInt(formData.windowCount, 10) || 0;
    if (count <= 0) return;
    
    const windowWidth = toMeters(parseFloat(formData.windowWidth) || 0);
    const windowHeight = toMeters(parseFloat(formData.windowHeight) || 0);
    
    // Цвет для окон
    ctx.fillStyle = 'rgba(59, 130, 246, 0.3)'; // голубой с прозрачностью
    ctx.strokeStyle = '#3b82f6'; // blue-500
    ctx.lineWidth = 2;
    
    // Размещаем окна равномерно по периметру
    // Для простоты размещаем их по верхней стене
    const totalWidth = roomLength - windowWidth * count;
    const spacing = totalWidth / (count + 1);
    
    for (let i = 0; i < count; i++) {
      const x = offsetX + spacing * (i + 1) + windowWidth * i;
      const y = offsetY;
      
      ctx.beginPath();
      ctx.rect(x * scale, y * scale, windowWidth * scale, windowHeight * 0.2 * scale);
      ctx.fill();
      ctx.stroke();
    }
    
    // Возвращаем стили к исходным
    ctx.strokeStyle = '#a21caf'; // fuchsia-600
    ctx.lineWidth = 4;
  };
  
  // Функция рисования дверей
  const drawDoors = (ctx, formData, offsetX, offsetY, roomLength, roomWidth, scale, toMeters) => {
    if (!formData.includeDoors) return;
    
    const count = parseInt(formData.doorCount, 10) || 0;
    if (count <= 0) return;
    
    const doorWidth = toMeters(parseFloat(formData.doorWidth) || 0);
    const doorHeight = toMeters(parseFloat(formData.doorHeight) || 0);
    
    // Цвет для дверей
    ctx.fillStyle = 'rgba(217, 119, 6, 0.3)'; // amber-600 с прозрачностью
    ctx.strokeStyle = '#d97706'; // amber-600
    ctx.lineWidth = 2;
    
    // Размещаем двери равномерно по периметру
    // Для простоты размещаем их по нижней стене
    const totalWidth = roomLength - doorWidth * count;
    const spacing = totalWidth / (count + 1);
    
    for (let i = 0; i < count; i++) {
      const x = offsetX + spacing * (i + 1) + doorWidth * i;
      const y = offsetY + roomWidth - doorWidth * 0.2;
      
      ctx.beginPath();
      ctx.rect(x * scale, y * scale, doorWidth * scale, doorWidth * 0.2 * scale);
      ctx.fill();
      ctx.stroke();
      
      // Дуга открытия двери
      ctx.beginPath();
      ctx.arc(x * scale, y * scale, doorWidth * scale, 0, Math.PI / 2);
      ctx.stroke();
    }
    
    // Возвращаем стили к исходным
    ctx.strokeStyle = '#a21caf'; // fuchsia-600
    ctx.lineWidth = 4;
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mt-8 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Eye className="w-5 h-5 mr-2 text-fuchsia-500" />
        Визуализация помещения
      </h3>
      
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex justify-center">
        <canvas
          ref={canvasRef}
          width={500}
          height={400}
          className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
        />
      </div>
      
      <div className="mt-4 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800">
        <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300">
          <span className="font-semibold">Примечание:</span> Визуализация представлена в упрощенном виде для наглядности. 
          Все элементы отображаются схематично, масштаб подобран автоматически. Для более детальной визуализации 
          рекомендуется использовать специализированные программы проектирования.
        </p>
      </div>
    </div>
  );
};

export default RoomVisualization;